import { Component, OnInit, ElementRef, ViewChild, ChangeDetectorRef, HostListener } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import * as XLSX from 'xlsx';
import { Globals } from '../../../../_globals/globals';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

interface LabelFieldOption {
  key: string;
  label: string;
  sample: string;
  lengthHint: string;
  synonyms: string[];
}

interface LabelTemplateToken {
  id: string;
  text: string;
  title: string;
  row: number;
  col: number;
  gridCol: number;
  gridRow: number;
  displayLength: number;
  multiline: number;
  valueAlign: string;
  fontSize: number;
  fontFamily: string;
  isBold: boolean;
  boldScope: string;
  dbFieldKey: string;
  logoFileName: string;
  logoDataUrl: string;
  logoOriginalWidth: number;
  logoOriginalHeight: number;
  logoTargetWidthMm: number;
  logoTargetHeightMm: number;
  barcodeType: string;
}

interface SheetCellView {
  row: number;
  col: number;
  text: string;
  tokenId: string;
}

interface SheetBounds {
  startRow: number;
  startCol: number;
  endRow: number;
  endCol: number;
}

interface ActiveGridBounds {
  minRow: number;
  maxRow: number;
  minCol: number;
  maxCol: number;
}

interface LabelTemplateSearchRow {
  name: string;
  updated_at: string;
}

@Component({
  templateUrl: './label-editor-shell.component.html'
})
export class LabelEditorShellComponent implements OnInit {
  @ViewChild('printableLabel', { static: false }) printableLabel: ElementRef<HTMLDivElement>;
  labelSizes: string[] = ['100 x 150 mm', '100 x 100 mm', '80 x 120 mm', 'A6', 'A5'];
  selectedLabelSize = '100 x 150 mm';
  selectedFileName = '';
  sheetName = '';
  statusText = 'Cargue un fichero XLS/XLSX para iniciar el mapeo.';
  statusType = 'is-info';
  fontFamilyOptions: string[] = ['Arial', 'Verdana', 'Tahoma', 'Helvetica', 'Times New Roman', 'Courier New'];
  alignOptions: Array<{ value: string; label: string }> = [
    { value: 'left', label: 'izquierda' },
    { value: 'center', label: 'centrado' },
    { value: 'right', label: 'derecha' }
  ];
  boldScopeOptions: Array<{ value: string; label: string }> = [
    { value: 'title', label: 'Título' },
    { value: 'value', label: 'Dato' },
    { value: 'both', label: 'Ambos' }
  ];
  previewModal = false;
  previewScalePxPerMm = 3.5;
  previewBatchno: number = null;
  previewBatchseq: number = null;
  previewUnitIndex = 1;
  previewFromIndex = 1;
  previewToIndex = 1;
  previewJumpIndex = 1;
  previewData: any = null;
  previewLoading = false;
  previewDataMsg = '';
  previewPdfGenerating = false;
  previewPdfProgressCurrent = 0;
  previewPdfProgressTotal = 0;
  templateName = '';
  templateMsg = '';
  templateSearchRows: LabelTemplateSearchRow[] = [];
  saveOverwriteModal = false;
  templatePickerModal = false;
  templatePickerPattern = '';
  templatePickerRows: LabelTemplateSearchRow[] = [];
  templatePickerLoading = false;
  templatePickerSelectedName = '';
  templatePickerPreviewLoading = false;
  templatePickerPreviewMsg = '';
  templatePickerPreviewLabelSize = '100 x 150 mm';
  templatePickerPreviewTokens: LabelTemplateToken[] = [];
  templatePickerPreviewPrecisionX = 1;
  templatePickerPreviewPrecisionY = 1;
  templatePickerPreviewActiveBounds: ActiveGridBounds = { minRow: 0, maxRow: 10, minCol: 0, maxCol: 6 };
  templatePickerPreviewData: any = null;
  templatePickerPreviewBatchno: number = null;
  templatePickerPreviewBatchseq: number = null;
  templatePickerPreviewDataLoading = false;
  templatePickerPreviewDataMsg = '';
  helpModal = false;
  helpSection = 'overview';
  tourModal = false;
  tourIndex = 0;
  tourModalCardStyle: any = {};

  dbFieldOptions: LabelFieldOption[] = [
    { key: 'batchno', label: 'Nº lote', sample: '260526', lengthHint: '6-8', synonyms: ['lote', 'batch', 'numero lote', 'n lote'] },
    { key: 'batchseq', label: 'Nº secuencia', sample: '28', lengthHint: '1-4', synonyms: ['secuencia', 'seq', 'n secuencia'] },
    { key: 'height', label: 'Alto', sample: '1500', lengthHint: '1-5', synonyms: ['alto', 'height'] },
    { key: 'width', label: 'Ancho', sample: '1200', lengthHint: '1-5', synonyms: ['ancho', 'width'] },
    { key: 'order_ref', label: 'Nº pedido (orderno/orderpos)', sample: 'A12345/001', lengthHint: '6-20', synonyms: ['pedido', 'orden', 'order'] },
    { key: 'barcode_no', label: 'Nº código barras incremental', sample: '3000001245', lengthHint: '8-14', synonyms: ['codigo barras', 'barcode', 'codigo'] },
    { key: 'width_add', label: 'Ancho_add', sample: '1204', lengthHint: '1-5', synonyms: ['ancho_add', 'width_add', 'ancho add'] },
    { key: 'height_add', label: 'Alto_add', sample: '1504', lengthHint: '1-5', synonyms: ['alto_add', 'height_add', 'alto add'] },
    { key: 'client_name', label: 'Cliente', sample: 'Cliente Demo', lengthHint: '1-80', synonyms: ['cliente', 'client'] },
    { key: 'client_no', label: 'Nº cliente', sample: 'C0001', lengthHint: '1-20', synonyms: ['n cliente', 'cliente id', 'codigo cliente'] },
    { key: 'material_name', label: 'Material', sample: 'Luna Incolora 4mm', lengthHint: '1-120', synonyms: ['material', 'vidrio'] },
    { key: 'material_code', label: 'Código material', sample: 'MAT-001', lengthHint: '1-30', synonyms: ['codigo material', 'cod material'] },
    { key: 'add_info', label: 'Info adicional', sample: 'Templado + Pulido', lengthHint: '1-255', synonyms: ['info adicional', 'observaciones', 'notas'] },
    { key: 'stock_length', label: 'Espesor', sample: '4', lengthHint: '1-4', synonyms: ['espesor', 'stock_length', 'thickness'] },
    { key: 'barcodeunits', label: 'Unidades', sample: '5', lengthHint: '1-4', synonyms: ['uds', 'unidades', 'cantidad'] },
    { key: 'kg_pane', label: 'Kilos panel', sample: '84.50', lengthHint: '1-10', synonyms: ['kg panel', 'kilos panel'] },
    { key: 'kg_unit', label: 'Kilos producto final', sample: '96.30', lengthHint: '1-10', synonyms: ['kg unidad', 'kg producto', 'kilos final'] },
    { key: 'worksteps', label: 'Lista de pasos (stepdesc)', sample: 'Corte > Pulido > Templado', lengthHint: '1-255', synonyms: ['pasos', 'stepdesc', 'workstep'] },
    { key: 'shape', label: 'Forma', sample: 'Rectangular', lengthHint: '1-60', synonyms: ['forma', 'shape'] },
    { key: 'shape_params', label: 'Parámetros forma', sample: 'R=120; A=35', lengthHint: '1-255', synonyms: ['parametros forma', 'shape params'] },
    { key: 'priority', label: 'Prioridad', sample: '1', lengthHint: '1-3', synonyms: ['prioridad', 'priority'] },
    { key: 'logo_image', label: 'Logotipo', sample: 'logo.png', lengthHint: 'fichero', synonyms: ['logo', 'logotipo', 'marca'] }
  ];

  tokens: LabelTemplateToken[] = [];
  selectedTokenId = '';
  previewRows: number[] = [];
  previewCols: number[] = [];
  previewCellsMap: { [key: string]: SheetCellView } = {};
  editorRows: number[] = [];
  editorCols: number[] = [];
  gridTokenMap: { [key: string]: string } = {};
  sheetBounds: SheetBounds = { startRow: 0, startCol: 0, endRow: 0, endCol: 0 };
  activeBounds: ActiveGridBounds = { minRow: 0, maxRow: 0, minCol: 0, maxCol: 0 };
  precisionOptions: number[] = [1, 2, 3, 4];
  precisionX = 1;
  precisionY = 1;
  precisionXDraft = 1;
  precisionYDraft = 1;

  constructor(
    private http: HttpClient,
    private globals: Globals,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.setDefaultTourModalPosition();
  }

  showHelpModal(valueIn: boolean) {
    this.helpModal = valueIn;
    if (valueIn) {
      this.helpSection = 'overview';
    }
  }

  setHelpSection(sectionIn: string) {
    this.helpSection = sectionIn;
  }

  isHelpSection(sectionIn: string): boolean {
    return this.helpSection === sectionIn;
  }

  downloadDefaultTemplate() {
    const a = document.createElement('a');
    a.href = 'assets/label-templates/Estandar.xls';
    a.download = 'Estandar.xls';
    a.target = '_blank';
    a.rel = 'noopener';
    a.click();
  }

  getTourSteps(): Array<{title: string, body: string, targetId: string}> {
    return [
      {
        title: '1) Cargar plantilla XLS',
        body: 'Empiece subiendo el XLS del cliente. El editor detecta textos mapeables y crea los datos iniciales.',
        targetId: 'leTopConfig'
      },
      {
        title: '2) Ajustar diseño en rejilla',
        body: 'Use la rejilla para mover datos. Si necesita más detalle, cambie Precisión X/Y y pulse Aplicar.',
        targetId: 'leGridPanel'
      },
      {
        title: '3) Configurar campo seleccionado',
        body: 'Asigne campo BD, longitud, multilínea, tipografía, negrita, alineación y parámetros especiales de logo/barcode.',
        targetId: 'leFieldPanel'
      },
      {
        title: '4) Guardar o cargar formatos',
        body: 'Guarde el formato con nombre, búsquelo y cárguelo después. También puede usar el Selector con vista previa.',
        targetId: 'leTemplateBar'
      },
      {
        title: '5) Validar con datos reales',
        body: 'Abra Vista previa, indique Nº lote y Nº secuencia, recorra etiquetas y genere PDF para probar en impresora.',
        targetId: 'lePreviewBtn'
      }
    ];
  }

  getCurrentTourStep(): {title: string, body: string, targetId: string} {
    const steps = this.getTourSteps();
    if (steps.length < 1) {
      return { title: '', body: '', targetId: '' };
    }
    if (this.tourIndex < 0) {
      this.tourIndex = 0;
    }
    if (this.tourIndex >= steps.length) {
      this.tourIndex = steps.length - 1;
    }
    return steps[this.tourIndex];
  }

  startTour() {
    this.helpModal = false;
    this.tourIndex = 0;
    this.tourModal = true;
    this.setDefaultTourModalPosition();
    this.focusTourTarget();
  }

  closeTour() {
    this.tourModal = false;
    this.setDefaultTourModalPosition();
  }

  nextTourStep() {
    const steps = this.getTourSteps();
    if (this.tourIndex < steps.length - 1) {
      this.tourIndex += 1;
      this.focusTourTarget();
      return;
    }
    this.closeTour();
  }

  prevTourStep() {
    if (this.tourIndex > 0) {
      this.tourIndex -= 1;
      this.focusTourTarget();
    }
  }

  private focusTourTarget() {
    const step = this.getCurrentTourStep();
    if (!step || !step.targetId) {
      return;
    }
    setTimeout(() => {
      const el = document.getElementById(step.targetId);
      if (!el) {
        this.setDefaultTourModalPosition();
        return;
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      setTimeout(() => {
        const oldOutline = el.style.outline;
        const oldOffset = el.style.outlineOffset;
        el.style.outline = '3px solid #3273dc';
        el.style.outlineOffset = '2px';
        this.positionTourModalNearElement(el);
        setTimeout(() => {
          el.style.outline = oldOutline;
          el.style.outlineOffset = oldOffset;
        }, 1400);
      }, 280);
    }, 120);
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (!this.tourModal) {
      return;
    }
    this.positionTourModalForCurrentStep();
  }

  positionTourModalForCurrentStep() {
    const step = this.getCurrentTourStep();
    if (!step || !step.targetId) {
      this.setDefaultTourModalPosition();
      return;
    }
    const el = document.getElementById(step.targetId);
    if (!el) {
      this.setDefaultTourModalPosition();
      return;
    }
    this.positionTourModalNearElement(el);
  }

  positionTourModalNearElement(el: HTMLElement) {
    if (!el) {
      this.setDefaultTourModalPosition();
      return;
    }

    const rect = el.getBoundingClientRect();
    const viewportW = window.innerWidth || 1280;
    const viewportH = window.innerHeight || 800;
    const gap = 16;
    const modalW = Math.max(360, Math.min(780, Math.floor(viewportW * 0.92)));
    const modalH = Math.min(320, Math.max(220, Math.floor(viewportH * 0.34)));

    const centerX = rect.left + (rect.width / 2);
    const centerY = rect.top + (rect.height / 2);

    let top = gap;
    if (centerY < (viewportH * 0.52)) {
      top = Math.min(viewportH - modalH - gap, rect.bottom + gap);
    } else {
      top = Math.max(gap, rect.top - modalH - gap);
    }

    let left = Math.max(gap, Math.floor((viewportW - modalW) / 2));
    if (viewportW >= 980) {
      if (centerX < (viewportW * 0.5)) {
        left = Math.min(viewportW - modalW - gap, Math.max(gap, rect.left));
      } else {
        left = Math.max(gap, Math.min(viewportW - modalW - gap, rect.right - modalW));
      }
    }

    this.tourModalCardStyle = {
      position: 'fixed',
      width: modalW + 'px',
      maxWidth: '92vw',
      margin: '0',
      top: Math.round(top) + 'px',
      left: Math.round(left) + 'px',
      zIndex: 1001
    };
  }

  setDefaultTourModalPosition() {
    const viewportW = window.innerWidth || 1280;
    const modalW = Math.max(360, Math.min(780, Math.floor(viewportW * 0.92)));
    const left = Math.max(12, Math.floor((viewportW - modalW) / 2));
    this.tourModalCardStyle = {
      position: 'fixed',
      width: modalW + 'px',
      maxWidth: '92vw',
      margin: '0',
      top: '20px',
      left: left + 'px',
      zIndex: 1001
    };
  }

  get selectedToken(): LabelTemplateToken {
    return this.tokens.find(t => t.id === this.selectedTokenId) || null;
  }

  onFileSelected(event: any) {
    const file: File = event && event.target && event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    this.selectedFileName = file.name;
    const reader = new FileReader();
    reader.onload = (ev: ProgressEvent) => {
      const target = ev.target as FileReader;
      const buffer = target.result as ArrayBuffer;
      this.parseWorkbook(buffer);
    };
    reader.onerror = () => {
      this.statusType = 'is-danger';
      this.statusText = 'No se pudo leer el fichero.';
    };
    reader.readAsArrayBuffer(file);
  }

  parseWorkbook(buffer: ArrayBuffer) {
    try {
      const workbook = XLSX.read(buffer, { type: 'array', cellStyles: true });
      if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
        this.statusType = 'is-danger';
        this.statusText = 'El fichero no contiene hojas.';
        return;
      }

      this.sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[this.sheetName];
      this.buildPreview(sheet);
      this.buildTokens(sheet);
      this.statusType = 'is-success';
      this.statusText = 'Fichero cargado. Revise y ajuste el mapeo en el panel derecho.';
    } catch (error) {
      this.statusType = 'is-danger';
      this.statusText = 'Error al parsear XLS/XLSX. Revise el formato del fichero.';
    }
  }

  buildPreview(sheet: XLSX.WorkSheet) {
    this.previewCellsMap = {};
    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1:A1');
    this.sheetBounds = {
      startRow: range.s.r,
      startCol: range.s.c,
      endRow: range.e.r,
      endCol: range.e.c
    };
    const maxCols = Math.min(range.e.c, 14);
    const maxRows = Math.min(range.e.r, 29);

    this.previewRows = [];
    this.previewCols = [];

    for (let c = 0; c <= maxCols; c++) {
      this.previewCols.push(c);
    }

    for (let r = 0; r <= maxRows; r++) {
      this.previewRows.push(r);
      for (let c = 0; c <= maxCols; c++) {
        const address = XLSX.utils.encode_cell({ r, c });
        const cell = sheet[address];
        const text = cell && cell.v !== undefined && cell.v !== null ? String(cell.v).trim() : '';
        this.previewCellsMap[this.cellMapKey(r, c)] = { row: r, col: c, text, tokenId: '' };
      }
    }
  }

  buildTokens(sheet: XLSX.WorkSheet) {
    this.tokens = [];
    const usedNames: { [key: string]: boolean } = {};
    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1:A1');
    let index = 1;

    for (let r = range.s.r; r <= range.e.r; r++) {
      for (let c = range.s.c; c <= range.e.c; c++) {
        const address = XLSX.utils.encode_cell({ r, c });
        const cell = sheet[address];
        if (!cell || cell.v === undefined || cell.v === null) {
          continue;
        }

        const raw = String(cell.v).trim();
        if (!this.isMappableText(raw)) {
          continue;
        }

        const uniqueKey = raw.toLowerCase() + '_' + r + '_' + c;
        if (usedNames[uniqueKey]) {
          continue;
        }
        usedNames[uniqueKey] = true;

        const guess = this.guessDbField(raw);
        const token: LabelTemplateToken = {
          id: 'tok_' + index++,
          text: raw,
          title: '',
          row: r,
          col: c,
          gridCol: 0,
          gridRow: 0,
          displayLength: 0,
          multiline: 0,
          valueAlign: 'left',
          fontSize: 11,
          fontFamily: 'Arial',
          isBold: false,
          boldScope: 'both',
          dbFieldKey: guess ? guess.key : '',
          logoFileName: '',
          logoDataUrl: '',
          logoOriginalWidth: 0,
          logoOriginalHeight: 0,
          logoTargetWidthMm: 30,
          logoTargetHeightMm: 12,
          barcodeType: 'interleaved_2_of_5'
        };
        this.tokens.push(token);

        const mapKey = this.cellMapKey(r, c);
        if (this.previewCellsMap[mapKey]) {
          this.previewCellsMap[mapKey].tokenId = token.id;
        }
      }
    }

    this.recalculateActiveBounds();
    this.tokens.forEach(token => {
      token.gridCol = (token.col - this.activeBounds.minCol) * this.precisionX;
      token.gridRow = (token.row - this.activeBounds.minRow) * this.precisionY;
    });
    this.normalizeTokensDefaults();
    this.rebuildEditorGrid();
    this.rebuildGridTokenMap();
    this.selectedTokenId = this.tokens.length > 0 ? this.tokens[0].id : '';
  }

  isMappableText(value: string): boolean {
    if (!value || value.length > 80) {
      return false;
    }
    const normalized = value.toLowerCase();
    if (normalized.indexOf('www.') >= 0 || normalized.indexOf('@') >= 0) {
      return false;
    }
    return /[a-záéíóúñ]/i.test(value);
  }

  guessDbField(rawText: string): LabelFieldOption {
    const normalized = this.normalizeText(rawText);
    let bestScore = 0;
    let bestField: LabelFieldOption = null;

    this.dbFieldOptions.forEach(field => {
      let score = 0;
      const fieldNorm = this.normalizeText(field.label);

      if (normalized === fieldNorm) {
        score += 10;
      }
      if (normalized.indexOf(fieldNorm) >= 0) {
        score += 6;
      }

      field.synonyms.forEach(s => {
        const syn = this.normalizeText(s);
        if (normalized === syn) {
          score += 8;
        } else if (normalized.indexOf(syn) >= 0) {
          score += 4;
        }
      });

      if (score > bestScore) {
        bestScore = score;
        bestField = field;
      }
    });

    return bestScore > 0 ? bestField : null;
  }

  normalizeText(value: string): string {
    return (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  selectToken(tokenId: string) {
    this.selectedTokenId = tokenId;
  }

  onEditorCellClick(row: number, col: number) {
    const tokenId = this.getTokenIdAtGridCell(row, col);
    if (tokenId) {
      this.selectToken(tokenId);
      return;
    }

    const logicalCol = Number((col / (this.precisionX || 1)).toFixed(4));
    const logicalRow = Number((row / (this.precisionY || 1)).toFixed(4));

    const token: LabelTemplateToken = {
      id: 'tok_custom_' + Date.now() + '_' + Math.floor(Math.random() * 10000),
      text: 'NUEVO DATO',
      title: '',
      row: 0,
      col: 0,
      gridCol: logicalCol,
      gridRow: logicalRow,
      displayLength: 0,
      multiline: 0,
      valueAlign: 'left',
      fontSize: 11,
      fontFamily: 'Arial',
      isBold: false,
      boldScope: 'both',
      dbFieldKey: '',
      logoFileName: '',
      logoDataUrl: '',
      logoOriginalWidth: 0,
      logoOriginalHeight: 0,
      logoTargetWidthMm: 30,
      logoTargetHeightMm: 12,
      barcodeType: 'interleaved_2_of_5'
    };

    this.tokens.push(token);
    this.normalizeTokenDefaults(token);
    this.rebuildGridTokenMap();
    this.selectedTokenId = token.id;
    this.templateMsg = 'Nuevo dato creado. Seleccione campo BD y parámetros.';
  }

  onDbFieldChanged(token: LabelTemplateToken) {
    if (!token) {
      return;
    }
    if (this.isLogoToken(token)) {
      if (!token.logoTargetWidthMm) {
        token.logoTargetWidthMm = 30;
      }
      if (!token.logoTargetHeightMm) {
        token.logoTargetHeightMm = 12;
      }
    }
    if (token.dbFieldKey === 'barcode_no' && !token.barcodeType) {
      token.barcodeType = 'interleaved_2_of_5';
    }
  }

  deleteSelectedToken() {
    if (!this.selectedToken) {
      return;
    }
    const removeId = this.selectedToken.id;
    this.tokens = this.tokens.filter(item => item.id !== removeId);
    this.rebuildGridTokenMap();
    if (this.tokens.length > 0) {
      this.selectedTokenId = this.tokens[0].id;
    } else {
      this.selectedTokenId = '';
    }
    this.statusType = 'is-info';
    this.statusText = 'Dato eliminado del diseño.';
  }

  getCellText(row: number, col: number): string {
    const cell = this.previewCellsMap[this.cellMapKey(row, col)];
    return cell ? cell.text : '';
  }

  getCellTokenId(row: number, col: number): string {
    const cell = this.previewCellsMap[this.cellMapKey(row, col)];
    return cell ? cell.tokenId : '';
  }

  getDbFieldLabel(fieldKey: string): string {
    const field = this.dbFieldOptions.find(item => item.key === fieldKey);
    return field ? field.label : 'Sin asignar';
  }

  getDbFieldSample(fieldKey: string): string {
    const field = this.dbFieldOptions.find(item => item.key === fieldKey);
    return field ? field.sample : '-';
  }

  getDbFieldLengthHint(fieldKey: string): string {
    const field = this.dbFieldOptions.find(item => item.key === fieldKey);
    return field ? field.lengthHint : '-';
  }

  getTokenById(tokenId: string): LabelTemplateToken {
    return this.tokens.find(t => t.id === tokenId);
  }

  isLogoToken(token: LabelTemplateToken): boolean {
    return !!token && token.dbFieldKey === 'logo_image';
  }

  onLogoFileSelected(event: any) {
    if (!this.selectedToken || !this.isLogoToken(this.selectedToken)) {
      return;
    }

    const file: File = event && event.target && event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    this.selectedToken.logoFileName = file.name;
    const reader = new FileReader();
    reader.onload = (ev: ProgressEvent) => {
      const target = ev.target as FileReader;
      const dataUrl = target.result as string;
      this.selectedToken.logoDataUrl = dataUrl;

      const image = new Image();
      image.onload = () => {
        this.selectedToken.logoOriginalWidth = image.width;
        this.selectedToken.logoOriginalHeight = image.height;
      };
      image.src = dataUrl;
    };
    reader.readAsDataURL(file);
  }

  openPreview() {
    this.normalizeTokensDefaults();
    this.normalizePreviewRange();
    if (!this.previewBatchno && this.previewData && this.previewData.batchno) {
      this.previewBatchno = Number(this.previewData.batchno);
    }
    if (!this.previewBatchseq && this.previewData && this.previewData.batchseq) {
      this.previewBatchseq = Number(this.previewData.batchseq);
    }
    this.previewDataMsg = '';
    this.previewModal = true;
  }

  closePreview() {
    this.previewModal = false;
  }

  async downloadPreviewPdf() {
    if (this.previewPdfGenerating) {
      return;
    }
    this.normalizePreviewRange();
    const dims = this.getLabelDimensionsMm();
    const labelEl = this.printableLabel && this.printableLabel.nativeElement
      ? this.printableLabel.nativeElement
      : null;
    if (!labelEl) {
      this.previewDataMsg = 'No se pudo generar PDF: vista previa no disponible.';
      return;
    }

    const from = this.getSafePreviewFrom();
    const to = this.getSafePreviewTo();
    const total = Math.max(0, to - from + 1);
    if (total < 1) {
      this.previewDataMsg = 'No hay etiquetas para generar.';
      return;
    }

    const originalIndex = this.previewUnitIndex;
    const originalData = this.previewData ? JSON.parse(JSON.stringify(this.previewData)) : null;
    const pages: string[] = [];

    this.previewPdfGenerating = true;
    this.previewPdfProgressCurrent = 0;
    this.previewPdfProgressTotal = total;
    this.previewDataMsg = 'Generando PDF...';

    try {
      for (let i = from; i <= to; i++) {
        const data = await this.fetchPreviewDataSilently(i);
        if (!data) {
          continue;
        }
        this.previewData = data;
        this.previewUnitIndex = Number(data.label_index) || i;
        this.cdr.detectChanges();
        const html = labelEl.outerHTML.replace(/src=\"unsafe:data:/g, 'src="data:');
        pages.push('<div class="page"><div class="sheet-wrap">' + html + '</div></div>');
        this.previewPdfProgressCurrent += 1;
        this.previewDataMsg = 'Generando PDF (' + this.previewPdfProgressCurrent + '/' + this.previewPdfProgressTotal + ')...';
      }

      if (pages.length < 1) {
        this.previewDataMsg = 'No se pudieron generar páginas.';
        return;
      }

      const popup = window.open('', '_blank', 'width=900,height=900');
      if (!popup) {
        this.previewDataMsg = 'No se pudo abrir ventana de impresión.';
        return;
      }

      const pageCss = '@page { size: ' + dims.width + 'mm ' + dims.height + 'mm; margin: 0; }';
      const docCss = 'html, body { margin: 0; padding: 0; width: ' + dims.width + 'mm; overflow: visible; background: #fff; }';
      const pagesCss = '.page { width: ' + dims.width + 'mm; height: ' + dims.height + 'mm; page-break-after: always; break-after: page; } .page:last-child { page-break-after: auto; break-after: auto; }';
      const wrapCss = '.sheet-wrap { width: ' + dims.width + 'mm; height: ' + dims.height + 'mm; overflow: hidden; }';
      const scaleCss = '.sheet-wrap > div { transform-origin: top left; }';

      popup.document.open();
      popup.document.write(
        '<!doctype html><html><head><meta charset="utf-8"><title>Etiqueta</title>' +
        '<style>' + pageCss + docCss + pagesCss + wrapCss + scaleCss + '</style>' +
        '</head><body>' + pages.join('') +
        '<script>window.onload=function(){setTimeout(function(){window.print();},150);};<\/script>' +
        '</body></html>'
      );
      popup.document.close();
      this.previewDataMsg = 'PDF generado (' + pages.length + ' etiquetas).';
    } finally {
      this.previewUnitIndex = originalIndex;
      this.previewData = originalData;
      this.cdr.detectChanges();
      this.previewPdfGenerating = false;
      this.previewPdfProgressCurrent = 0;
      this.previewPdfProgressTotal = 0;
    }
  }

  async fetchPreviewDataSilently(index: number): Promise<any | null> {
    const batchno = Number(this.previewBatchno);
    const batchseq = Number(this.previewBatchseq);
    const labelIndex = Math.max(1, Math.floor(Number(index) || 1));
    if (!Number.isFinite(batchno) || batchno <= 0) {
      return null;
    }

    let params = new HttpParams()
      .set('batchno', batchno.toString())
      .set('label_index', labelIndex.toString())
      .set('_ts', Date.now().toString());
    if (Number.isFinite(batchseq) && batchseq > 0) {
      params = params.set('batchseq', Math.floor(batchseq).toString());
    }

    try {
      const res: any = await this.http.get<any>(this.globals.apiUrl + '/label_preview_data', { params }).toPromise();
      if (res && res.ok && res.data) {
        return res.data;
      }
    } catch (e) {}
    return null;
  }

  getLabelDimensionsMm(): { width: number; height: number } {
    if (this.selectedLabelSize === '100 x 150 mm') {
      return { width: 100, height: 150 };
    }
    if (this.selectedLabelSize === '100 x 100 mm') {
      return { width: 100, height: 100 };
    }
    if (this.selectedLabelSize === '80 x 120 mm') {
      return { width: 80, height: 120 };
    }
    if (this.selectedLabelSize === 'A6') {
      return { width: 105, height: 148 };
    }
    return { width: 148, height: 210 };
  }

  getLabelWidthPx(): number {
    return this.getLabelDimensionsMm().width * this.previewScalePxPerMm;
  }

  getLabelHeightPx(): number {
    return this.getLabelDimensionsMm().height * this.previewScalePxPerMm;
  }

  getTokenLeftPx(token: LabelTemplateToken): number {
    const metrics = this.getGridLayoutMetrics();
    const gridCol = this.getEffectiveGridCol(token);
    return metrics.marginX + (gridCol * metrics.cellWidth) + 2;
  }

  getTokenTopPx(token: LabelTemplateToken): number {
    const metrics = this.getGridLayoutMetrics();
    const gridRow = this.getEffectiveGridRow(token);
    return metrics.marginY + (gridRow * metrics.cellHeight) + 2;
  }

  getLogoWidthPx(token: LabelTemplateToken): number {
    return Math.max(12, token.logoTargetWidthMm * this.previewScalePxPerMm);
  }

  getLogoHeightPx(token: LabelTemplateToken): number {
    return Math.max(8, token.logoTargetHeightMm * this.previewScalePxPerMm);
  }

  getBarcodeCaptionFontSize(token: LabelTemplateToken): number {
    const base = token && token.fontSize ? token.fontSize : 11;
    return base > 11 ? base - 1 : 10;
  }

  getGridLayoutMetrics(): { marginX: number; marginY: number; cellWidth: number; cellHeight: number } {
    const labelWidth = this.getLabelWidthPx();
    const labelHeight = this.getLabelHeightPx();
    const marginX = 10;
    const marginY = 10;
    const baseCols = this.getBaseCols();
    const baseRows = this.getBaseRows();
    const cols = Math.max(1, baseCols);
    const rows = Math.max(1, baseRows);
    const innerWidth = Math.max(20, labelWidth - (2 * marginX));
    const innerHeight = Math.max(20, labelHeight - (2 * marginY));
    return {
      marginX,
      marginY,
      cellWidth: innerWidth / cols,
      cellHeight: innerHeight / rows
    };
  }

  getTokenBoxWidthPx(): number {
    const metrics = this.getGridLayoutMetrics();
    return Math.max(20, metrics.cellWidth - 4);
  }

  getTokenTitleWidthPx(token: LabelTemplateToken): number {
    if (!token || !token.title) {
      return 0;
    }
    const font = Math.max(8, Number(token.fontSize) || 11);
    return Math.ceil((token.title.length + 2) * font * 0.55);
  }

  getTokenContainerWidthPx(token: LabelTemplateToken): number {
    const left = this.getTokenLeftPx(token);
    const available = Math.max(24, this.getLabelWidthPx() - left - 8);
    const base = this.getTokenBoxWidthPx();
    const titleWidth = this.getTokenTitleWidthPx(token);
    if (!token || !token.displayLength || Number(token.displayLength) <= 0) {
      return Math.min(available, base);
    }
    const chars = Number(token.displayLength);
    const font = Math.max(8, Number(token.fontSize) || 11);
    const valueWidth = Math.ceil((chars * font * 0.58) + 10);
    const requested = titleWidth + valueWidth;
    return Math.max(30, Math.min(available, requested));
  }

  getTokenValueAreaWidthPx(token: LabelTemplateToken): number {
    const titleWidth = this.getTokenTitleWidthPx(token);
    const containerWidth = this.getTokenContainerWidthPx(token);
    return Math.max(16, containerWidth - titleWidth);
  }

  getTokenTextMaxWidthPx(token: LabelTemplateToken): number {
    return this.getTokenContainerWidthPx(token);
  }

  getValueAlignedLeftPx(token: LabelTemplateToken): number {
    return this.getTokenLeftPx(token);
  }

  getBarcodeWidthPx(token: LabelTemplateToken): number {
    const maxTextWidth = this.getBarcodeBlockWidthPx(token);
    const digits = Math.max(4, this.getBarcodeNumericValue(token).length);
    const font = Math.max(10, Number(token && token.fontSize ? token.fontSize : 11));
    const desired = Math.ceil((digits * 8) + (font * 1.8) + 18);
    return Math.max(48, Math.min(maxTextWidth, desired, this.getLabelWidthPx() - this.getTokenLeftPx(token) - 8));
  }

  getBarcodeHeightPx(token: LabelTemplateToken): number {
    const metrics = this.getGridLayoutMetrics();
    const font = Math.max(10, Number(token && token.fontSize ? token.fontSize : 11));
    const desired = Math.ceil(font * 2.05);
    return Math.max(22, Math.min(78, desired, metrics.cellHeight * 1.85));
  }

  getBarcodeNumericWidthPx(token: LabelTemplateToken): number {
    const value = this.getBarcodeNumericValue(token) || '';
    const font = this.getBarcodeCaptionFontSize(token);
    return Math.max(24, Math.ceil((value.length * font * 0.62) + 8));
  }

  getBarcodeInnerWidthPx(token: LabelTemplateToken): number {
    return this.getBarcodeWidthPx(token);
  }

  getBarcodeInnerOffsetPx(token: LabelTemplateToken): number {
    const block = this.getBarcodeBlockWidthPx(token);
    const inner = this.getBarcodeInnerWidthPx(token);
    const align = this.resolveTokenAlign(token);
    if (align === 'right') {
      return Math.max(0, block - inner);
    }
    if (align === 'center') {
      return Math.max(0, (block - inner) / 2);
    }
    return 0;
  }

  getBarcodeBlockWidthPx(token: LabelTemplateToken): number {
    return this.getTokenContainerWidthPx(token);
  }

  getBarcodeBlockLeftPx(token: LabelTemplateToken): number {
    const baseLeft = this.getTokenLeftPx(token);
    const align = this.resolveTokenAlign(token);
    if (align === 'left') {
      return Math.max(2, baseLeft - 8);
    }
    if (align === 'center') {
      return Math.max(2, baseLeft - 4);
    }
    return baseLeft;
  }

  applyPrecision() {
    const nextX = Number(this.precisionXDraft) || 1;
    const nextY = Number(this.precisionYDraft) || 1;
    const prevX = this.precisionX || 1;
    const prevY = this.precisionY || 1;

    if (prevX === nextX && prevY === nextY) {
      return;
    }

    this.precisionX = nextX;
    this.precisionY = nextY;
    this.rebuildEditorGrid();
    this.rebuildGridTokenMap();
    this.statusType = 'is-info';
    this.statusText = 'Precisión aplicada: X' + this.precisionX + ' / Y' + this.precisionY + '.';
  }

  getMaxGridCol(): number {
    return this.getBaseCols() - 1;
  }

  getMaxGridRow(): number {
    return this.getBaseRows() - 1;
  }

  getBaseCols(): number {
    return Math.max(1, this.activeBounds.maxCol - this.activeBounds.minCol + 1);
  }

  getBaseRows(): number {
    return Math.max(1, this.activeBounds.maxRow - this.activeBounds.minRow + 1);
  }

  recalculateActiveBounds() {
    if (!this.tokens || this.tokens.length < 1) {
      this.activeBounds = {
        minRow: this.sheetBounds.startRow,
        maxRow: this.sheetBounds.endRow,
        minCol: this.sheetBounds.startCol,
        maxCol: this.sheetBounds.endCol
      };
      return;
    }

    let minRow = Number.MAX_SAFE_INTEGER;
    let maxRow = 0;
    let minCol = Number.MAX_SAFE_INTEGER;
    let maxCol = 0;

    this.tokens.forEach(token => {
      minRow = Math.min(minRow, token.row);
      maxRow = Math.max(maxRow, token.row);
      minCol = Math.min(minCol, token.col);
      maxCol = Math.max(maxCol, token.col);
    });

    // Margen de 1 celda para que no quede al borde.
    minRow = Math.max(0, minRow - 1);
    minCol = Math.max(0, minCol - 1);
    maxRow = maxRow + 1;
    maxCol = maxCol + 1;

    this.activeBounds = { minRow, maxRow, minCol, maxCol };
  }

  rebuildEditorGrid() {
    this.editorCols = [];
    this.editorRows = [];
    const maxCol = (this.getBaseCols() * this.precisionX) - 1;
    const maxRow = (this.getBaseRows() * this.precisionY) - 1;
    for (let c = 0; c <= maxCol; c++) {
      this.editorCols.push(c);
    }
    for (let r = 0; r <= maxRow; r++) {
      this.editorRows.push(r);
    }
  }

  rebuildGridTokenMap() {
    this.gridTokenMap = {};
    this.tokens.forEach(token => {
      if (!token) return;
      const col = Math.max(0, Math.round(this.getEffectiveGridCol(token)));
      const row = Math.max(0, Math.round(this.getEffectiveGridRow(token)));
      this.gridTokenMap[this.gridCellKey(row, col)] = token.id;
    });
  }

  gridCellKey(row: number, col: number): string {
    return row + '_' + col;
  }

  getTokenIdAtGridCell(row: number, col: number): string {
    return this.gridTokenMap[this.gridCellKey(row, col)] || '';
  }

  getTokenAtGridCell(row: number, col: number): LabelTemplateToken {
    const tokenId = this.getTokenIdAtGridCell(row, col);
    return tokenId ? this.getTokenById(tokenId) : null;
  }

  getGridCellText(row: number, col: number): string {
    const token = this.getTokenAtGridCell(row, col);
    return token ? token.text : '';
  }

  getGridCellFieldLabel(row: number, col: number): string {
    const token = this.getTokenAtGridCell(row, col);
    return token ? this.getDbFieldLabel(token.dbFieldKey) : '';
  }

  onTokenGridPositionChanged(token: LabelTemplateToken) {
    if (!token) {
      return;
    }
    const maxCol = this.getMaxGridCol();
    const maxRow = this.getMaxGridRow();
    token.gridCol = Math.max(0, Math.min(maxCol, Number(token.gridCol) || 0));
    token.gridRow = Math.max(0, Math.min(maxRow, Number(token.gridRow) || 0));
    this.rebuildGridTokenMap();
  }

  getEffectiveGridCol(token: LabelTemplateToken): number {
    const logical = token && token.gridCol !== undefined && token.gridCol !== null
      ? Number(token.gridCol)
      : (token.col - this.activeBounds.minCol);
    return logical;
  }

  getEffectiveGridRow(token: LabelTemplateToken): number {
    const logical = token && token.gridRow !== undefined && token.gridRow !== null
      ? Number(token.gridRow)
      : (token.row - this.activeBounds.minRow);
    return logical;
  }

  getGridStepX(): number {
    return 1 / (this.precisionX || 1);
  }

  getGridStepY(): number {
    return 1 / (this.precisionY || 1);
  }

  getEditorCellWidthPx(): number {
    const baseWidth = 80;
    const px = baseWidth / (this.precisionX || 1);
    return Math.max(20, Math.round(px));
  }

  getEditorCellHeightPx(): number {
    const baseHeight = 34;
    const px = baseHeight / (this.precisionY || 1);
    return Math.max(16, Math.round(px));
  }

  loadPreviewData() {
    this.loadPreviewDataByIndex(1, true);
  }

  async loadPreviewDataByIndex(index: number, initializeRange: boolean): Promise<boolean> {
    const batchno = Number(this.previewBatchno);
    const batchseq = Number(this.previewBatchseq);
    const labelIndex = Math.max(1, Math.floor(Number(index) || 1));

    if (!Number.isFinite(batchno) || batchno <= 0) {
      this.previewDataMsg = 'Indique Nº lote válido.';
      return false;
    }

    this.previewLoading = true;
    this.previewDataMsg = '';

    let params = new HttpParams()
      .set('batchno', batchno.toString())
      .set('label_index', labelIndex.toString())
      .set('_ts', Date.now().toString());
    if (Number.isFinite(batchseq) && batchseq > 0) {
      params = params.set('batchseq', Math.floor(batchseq).toString());
    }

    try {
      const res: any = await this.http.get<any>(this.globals.apiUrl + '/label_preview_data', { params }).toPromise();
      this.previewLoading = false;
      if (res && res.ok && res.data) {
        this.previewData = res.data;
        this.previewUnitIndex = Number(res.data.label_index) || labelIndex;
        if (initializeRange) {
          this.previewFromIndex = 1;
          this.previewToIndex = Number(res.data.labels_total) || this.previewUnitIndex;
        }
        this.normalizePreviewRange();
        this.previewJumpIndex = this.previewUnitIndex;
        this.previewDataMsg = 'Datos cargados correctamente.';
        return true;
      }
      this.previewData = null;
      this.previewDataMsg = (res && res.msg) ? res.msg : 'Sin datos para esa referencia.';
      return false;
    } catch (e) {
      this.previewLoading = false;
      this.previewData = null;
      this.previewDataMsg = 'Error al consultar datos de vista previa.';
      return false;
    }
  }

  getPreviewValueForToken(token: LabelTemplateToken): string {
    if (!token || !token.dbFieldKey) {
      return '';
    }
    if (token.dbFieldKey === 'barcodeunits') {
      const total = this.previewData && this.previewData.label_unit_total
        ? Number(this.previewData.label_unit_total)
        : this.getRenderTotalUnits();
      if (total <= 0) {
        return '';
      }
      const current = this.previewData && this.previewData.label_unit_index
        ? Number(this.previewData.label_unit_index)
        : this.getCurrentUnitIndex();
      return 'UDS: ' + current + '/' + total;
    }
    if (token.dbFieldKey === 'barcode_no') {
      const base = this.previewData && this.previewData[token.dbFieldKey] !== undefined ? this.previewData[token.dbFieldKey] : '';
      if (!base) {
        return '';
      }
      return String(base);
    }
    if (!this.previewData) {
      return '';
    }
    const value = this.previewData[token.dbFieldKey];
    if (value === undefined || value === null) {
      return '';
    }
    return String(value);
  }

  getBarcodeNumericValue(token: LabelTemplateToken): string {
    const raw = this.applyTokenDisplayLength(token, this.getPreviewValueForToken(token));
    const onlyDigits = (raw || '').replace(/[^0-9]/g, '');
    if (!onlyDigits) {
      return '';
    }
    return onlyDigits.length % 2 === 0 ? onlyDigits : ('0' + onlyDigits);
  }

  getBarcodeSvgDataUrl(token: LabelTemplateToken): string {
    const svg = this.getBarcodeSvgMarkup(token);
    if (!svg) {
      return '';
    }
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
  }

  getBarcodeSvgMarkup(token: LabelTemplateToken): string {
    const value = this.getBarcodeNumericValue(token);
    if (!value) {
      return '';
    }
    if (token.barcodeType !== 'interleaved_2_of_5') {
      return '';
    }

    const patterns = {
      '0': 'nnwwn',
      '1': 'wnnnw',
      '2': 'nwnnw',
      '3': 'wwnnn',
      '4': 'nnwnw',
      '5': 'wnwnn',
      '6': 'nwwnn',
      '7': 'nnnww',
      '8': 'wnnwn',
      '9': 'nwnwn'
    };

    const narrow = 2;
    const wide = 5;
    const quiet = 10 * narrow;
    const barHeight = 62;
    let x = quiet;
    let bars = '';

    const addBar = (width: number) => {
      bars += '<rect x="' + x + '" y="0" width="' + width + '" height="' + barHeight + '" fill="#000"/>';
      x += width;
    };
    const addSpace = (width: number) => {
      x += width;
    };
    const w = (code: string) => (code === 'w' ? wide : narrow);

    // Start pattern: bar-space-bar-space (narrow).
    addBar(narrow); addSpace(narrow); addBar(narrow); addSpace(narrow);

    for (let i = 0; i < value.length; i += 2) {
      const a = patterns[value[i]];
      const b = patterns[value[i + 1]];
      for (let j = 0; j < 5; j++) {
        addBar(w(a[j]));
        addSpace(w(b[j]));
      }
    }

    // Stop pattern: wide bar, narrow space, narrow bar.
    addBar(wide); addSpace(narrow); addBar(narrow);
    x += quiet;

    const svgWidth = x;
    return '<svg xmlns="http://www.w3.org/2000/svg" width="' + svgWidth + '" height="' + barHeight + '" viewBox="0 0 ' + svgWidth + ' ' + barHeight + '">' + bars + '</svg>';
  }

  getBarcodeSvgSafeUrl(token: LabelTemplateToken): SafeUrl {
    const dataUrl = this.getBarcodeSvgDataUrl(token);
    if (!dataUrl) {
      return '';
    }
    return this.sanitizer.bypassSecurityTrustUrl(dataUrl);
  }

  applyTokenDisplayLength(token: LabelTemplateToken, value: string): string {
    if (!token) {
      return value || '';
    }
    const maxLen = Number(token.displayLength) || 0;
    if (maxLen <= 0) {
      return value || '';
    }
    const txt = value || '';
    return txt.length > maxLen ? txt.substring(0, maxLen) : txt;
  }

  getTokenDisplayLines(token: LabelTemplateToken): string[] {
    const text = this.getTokenDisplayTextRaw(token) || '';
    const maxLines = Number(token && token.multiline ? token.multiline : 0);
    const lineLen = Number(token && token.displayLength ? token.displayLength : 0);
    if (maxLines < 2 || lineLen <= 0) {
      return [this.applyTokenDisplayLength(token, text)];
    }

    const totalLen = lineLen * maxLines;
    const limited = text.substring(0, totalLen);
    const chunks: string[] = [];
    for (let i = 0; i < limited.length; i += lineLen) {
      if (chunks.length >= maxLines) {
        break;
      }
      chunks.push(limited.substring(i, i + lineLen));
    }
    return chunks.length > 0 ? chunks : [''];
  }

  getTokenDisplayText(token: LabelTemplateToken): string {
    const raw = this.getTokenDisplayTextRaw(token);
    return this.applyTokenDisplayLength(token, raw);
  }

  getTokenTitleText(token: LabelTemplateToken): string {
    if (!token || !token.title) {
      return '';
    }
    return token.title + ' ';
  }

  getTokenValueRaw(token: LabelTemplateToken): string {
    if (!token) {
      return '';
    }
    const mappedValue = this.getPreviewValueForToken(token);
    if (mappedValue !== null && mappedValue !== undefined && mappedValue !== '') {
      return mappedValue.toString();
    }
    return token.text || '';
  }

  getTokenValueLines(token: LabelTemplateToken): string[] {
    const text = this.getTokenValueRaw(token) || '';
    const maxLines = Number(token && token.multiline ? token.multiline : 0);
    const lineLen = Number(token && token.displayLength ? token.displayLength : 0);
    if (maxLines < 2 || lineLen <= 0) {
      return [this.applyTokenDisplayLength(token, text)];
    }

    const totalLen = lineLen * maxLines;
    const limited = text.substring(0, totalLen);
    const chunks: string[] = [];
    for (let i = 0; i < limited.length; i += lineLen) {
      if (chunks.length >= maxLines) {
        break;
      }
      chunks.push(limited.substring(i, i + lineLen));
    }
    return chunks.length > 0 ? chunks : [''];
  }

  getTokenValueText(token: LabelTemplateToken): string {
    return this.getTokenValueLines(token).join('\n');
  }

  getTokenValueAlign(token: LabelTemplateToken): string {
    return this.resolveTokenAlign(token);
  }

  getTokenTitleFontWeight(token: LabelTemplateToken): string {
    const scope = this.resolveBoldScope(token);
    return token && token.isBold && (scope === 'title' || scope === 'both') ? '700' : '400';
  }

  getTokenValueFontWeight(token: LabelTemplateToken): string {
    const scope = this.resolveBoldScope(token);
    return token && token.isBold && (scope === 'value' || scope === 'both') ? '700' : '400';
  }

  getTokenDisplayTextRaw(token: LabelTemplateToken): string {
    if (!token) {
      return '';
    }
    if (this.isLogoToken(token)) {
      return token.logoFileName || token.text;
    }
    const value = this.getTokenValueRaw(token);
    if (token.dbFieldKey === 'barcodeunits') {
      return value;
    }
    return token.title ? (token.title + ': ' + value) : value;
  }

  getTokenTextMaxHeightPx(token: LabelTemplateToken): number {
    const lines = this.getTokenDisplayLines(token).length;
    const lineHeight = this.getTokenLineHeightPx(token);
    return Math.max(lineHeight + 4, (lines * lineHeight) + 6);
  }

  getTokenLineHeightPx(token: LabelTemplateToken): number {
    const font = Math.max(8, Number(token && token.fontSize ? token.fontSize : 11));
    return Math.ceil(font * 1.32);
  }

  getUnitsTotal(): number {
    const raw = this.previewData && this.previewData.labels_total !== undefined
      ? Number(this.previewData.labels_total)
      : (this.previewData && this.previewData.barcodeunits !== undefined ? Number(this.previewData.barcodeunits) : 0);
    return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 0;
  }

  getRenderTotalUnits(): number {
    const raw = this.getUnitsTotal();
    const rangeMax = Math.max(1, Number(this.previewToIndex) || 1);
    return Math.max(raw, rangeMax);
  }

  getCurrentUnitIndex(): number {
    const total = this.getRenderTotalUnits();
    if (total <= 0) {
      return 1;
    }
    const current = Number(this.previewUnitIndex) || 1;
    const from = this.getSafePreviewFrom();
    const to = this.getSafePreviewTo();
    return Math.max(from, Math.min(to, Math.floor(current)));
  }

  getSafePreviewFrom(): number {
    const max = Math.max(this.getRenderTotalUnits(), Number(this.previewToIndex) || 1);
    if (max <= 0) {
      return 1;
    }
    const from = Number(this.previewFromIndex) || 1;
    return Math.max(1, Math.min(max, Math.floor(from)));
  }

  getSafePreviewTo(): number {
    const max = Math.max(this.getRenderTotalUnits(), Number(this.previewToIndex) || 1);
    if (max <= 0) {
      return 1;
    }
    const from = this.getSafePreviewFrom();
    const to = Number(this.previewToIndex) || max;
    return Math.max(from, Math.min(max, Math.floor(to)));
  }

  normalizePreviewRange() {
    const max = Math.max(this.getRenderTotalUnits(), Number(this.previewToIndex) || 1);
    if (max <= 0) {
      this.previewFromIndex = 1;
      this.previewToIndex = 1;
      this.previewUnitIndex = 1;
      this.previewJumpIndex = 1;
      return;
    }
    this.previewFromIndex = this.getSafePreviewFrom();
    this.previewToIndex = this.getSafePreviewTo();
    if (this.previewUnitIndex < this.previewFromIndex || this.previewUnitIndex > this.previewToIndex) {
      this.previewUnitIndex = this.previewFromIndex;
    }
    this.previewJumpIndex = this.previewUnitIndex;
  }

  getPreviewPageCount(): number {
    const from = this.getSafePreviewFrom();
    const to = this.getSafePreviewTo();
    return Math.max(1, (to - from + 1));
  }

  getPreviewPagePosition(): number {
    const from = this.getSafePreviewFrom();
    return Math.max(1, this.getCurrentUnitIndex() - from + 1);
  }

  goPreviewFirst() {
    this.normalizePreviewRange();
    this.loadPreviewDataByIndex(this.previewFromIndex, false);
  }

  goPreviewLast() {
    this.normalizePreviewRange();
    this.loadPreviewDataByIndex(this.previewToIndex, false);
  }

  goPreviewPrev() {
    this.normalizePreviewRange();
    const next = Math.max(this.previewFromIndex, this.getCurrentUnitIndex() - 1);
    this.loadPreviewDataByIndex(next, false);
  }

  goPreviewNext() {
    this.normalizePreviewRange();
    const next = Math.min(this.previewToIndex, this.getCurrentUnitIndex() + 1);
    this.loadPreviewDataByIndex(next, false);
  }

  goPreviewToIndex() {
    this.normalizePreviewRange();
    const target = Number(this.previewJumpIndex) || this.previewFromIndex;
    const idx = Math.max(this.previewFromIndex, Math.min(this.previewToIndex, Math.floor(target)));
    this.loadPreviewDataByIndex(idx, false);
  }

  saveTemplate() {
    if (!this.templateName || this.templateName.trim() === '') {
      this.templateMsg = 'Indique nombre de formato.';
      return;
    }
    this.checkTemplateExistsAndSave();
  }

  confirmOverwriteSave() {
    this.saveOverwriteModal = false;
    this.attemptSaveTemplate(true);
  }

  cancelOverwriteSave() {
    this.saveOverwriteModal = false;
  }

  attemptSaveTemplate(forceOverwrite: boolean) {
    const payload = {
      name: this.templateName.trim(),
      label_size: this.selectedLabelSize,
      precision_x: this.precisionX,
      precision_y: this.precisionY,
      force_overwrite: forceOverwrite ? 1 : 0,
      config_json: JSON.stringify({
        previewScalePxPerMm: this.previewScalePxPerMm,
        tokens: this.tokens
      })
    };

    this.http.post<any>(this.globals.apiUrl + '/label_template_save', payload)
      .subscribe(
        (res: any) => {
          if (res && res.ok) {
            this.templateMsg = 'Formato guardado.';
            return;
          }
          if (res && res.overwrite_required) {
            this.saveOverwriteModal = true;
            return;
          }
          this.templateMsg = res && res.msg ? res.msg : 'Error guardando formato.';
        },
        () => { this.templateMsg = 'Error guardando formato.'; }
      );
  }

  checkTemplateExistsAndSave() {
    const name = this.templateName.trim();
    let params = new HttpParams()
      .set('name', name)
      .set('_ts', Date.now().toString());

    this.http.get<any>(this.globals.apiUrl + '/label_template_load', { params })
      .subscribe(
        (res: any) => {
          const exists = !!(res && res.ok && res.data);
          if (exists) {
            this.saveOverwriteModal = true;
            return;
          }
          this.attemptSaveTemplate(false);
        },
        () => {
          // Si falla la comprobación, intentamos guardar directamente.
          this.attemptSaveTemplate(false);
        }
      );
  }

  loadTemplate() {
    if (!this.templateName || this.templateName.trim() === '') {
      this.templateMsg = 'Indique nombre de formato.';
      return;
    }
    let params = new HttpParams()
      .set('name', this.templateName.trim())
      .set('_ts', Date.now().toString());
    this.http.get<any>(this.globals.apiUrl + '/label_template_load', { params })
      .subscribe(
        (res: any) => {
          if (!res || !res.ok || !res.data || !res.data.config_json) {
            this.templateMsg = 'Formato no encontrado.';
            return;
          }
          try {
            const cfg = JSON.parse(res.data.config_json);
            this.selectedLabelSize = res.data.label_size || this.selectedLabelSize;
            this.precisionX = Number(res.data.precision_x) || 1;
            this.precisionY = Number(res.data.precision_y) || 1;
            this.precisionXDraft = this.precisionX;
            this.precisionYDraft = this.precisionY;
            this.previewScalePxPerMm = Number(cfg.previewScalePxPerMm) || this.previewScalePxPerMm;
            this.tokens = Array.isArray(cfg.tokens) ? cfg.tokens : [];
            this.normalizeTokensDefaults();
            this.recalculateActiveBounds();
            this.rebuildEditorGrid();
            this.rebuildGridTokenMap();
            this.selectedTokenId = this.tokens.length > 0 ? this.tokens[0].id : '';
            this.templateMsg = 'Formato cargado.';
          } catch (e) {
            this.templateMsg = 'Formato inválido.';
          }
        },
        () => { this.templateMsg = 'Error cargando formato.'; }
      );
  }

  searchTemplates() {
    let params = new HttpParams().set('pattern', this.templateName ? this.templateName.trim() : '');
    this.http.get<any>(this.globals.apiUrl + '/label_template_list', { params })
      .subscribe(
        (res: any) => {
          this.templateSearchRows = res && res.ok && Array.isArray(res.rows) ? res.rows : [];
        },
        () => {
          this.templateSearchRows = [];
        }
      );
  }

  pickTemplateName(name: string) {
    this.templateName = name || '';
  }

  deleteTemplate(nameIn?: string, fromPicker?: boolean) {
    const name = (nameIn || this.templateName || '').toString().trim();
    if (!name) {
      this.templateMsg = 'Indique nombre de formato.';
      return;
    }
    const ok = window.confirm('¿Borrar formato "' + name + '"?');
    if (!ok) {
      return;
    }
    let params = new HttpParams()
      .set('name', name)
      .set('_ts', Date.now().toString());
    this.http.delete<any>(this.globals.apiUrl + '/label_template_delete', { params })
      .subscribe(
        (res: any) => {
          if (!res || !res.ok) {
            const msg = res && res.msg ? res.msg : 'Error borrando formato.';
            if (fromPicker) {
              this.templatePickerPreviewDataMsg = msg;
            } else {
              this.templateMsg = msg;
            }
            return;
          }
          if (fromPicker) {
            this.templatePickerPreviewDataMsg = 'Formato borrado.';
            this.templatePickerSelectedName = '';
            this.templatePickerPreviewTokens = [];
            this.templatePickerPreviewMsg = 'Seleccione un formato.';
            this.searchTemplatePicker();
            if (this.templateName === name) {
              this.templateName = '';
            }
            return;
          }
          this.templateMsg = 'Formato borrado.';
          if (this.templateName === name) {
            this.templateName = '';
          }
          this.searchTemplates();
        },
        () => {
          const msg = 'Error borrando formato.';
          if (fromPicker) {
            this.templatePickerPreviewDataMsg = msg;
          } else {
            this.templateMsg = msg;
          }
        }
      );
  }

  openTemplatePicker() {
    this.templatePickerModal = true;
    this.templatePickerPattern = this.templateName || '';
    this.templatePickerPreviewMsg = '';
    this.templatePickerPreviewDataMsg = '';
    this.searchTemplatePicker();
  }

  closeTemplatePicker() {
    this.templatePickerModal = false;
  }

  searchTemplatePicker() {
    this.templatePickerLoading = true;
    let params = new HttpParams().set('pattern', this.templatePickerPattern ? this.templatePickerPattern.trim() : '');
    this.http.get<any>(this.globals.apiUrl + '/label_template_list', { params })
      .subscribe(
        (res: any) => {
          this.templatePickerLoading = false;
          this.templatePickerRows = res && res.ok && Array.isArray(res.rows) ? res.rows : [];
          if (this.templatePickerRows.length > 0) {
            const first = this.templatePickerRows[0];
            this.pickTemplateForPreview(first.name);
          } else {
            this.templatePickerSelectedName = '';
            this.templatePickerPreviewTokens = [];
            this.templatePickerPreviewMsg = 'Sin formatos para el filtro.';
          }
        },
        () => {
          this.templatePickerLoading = false;
          this.templatePickerRows = [];
          this.templatePickerSelectedName = '';
          this.templatePickerPreviewTokens = [];
          this.templatePickerPreviewMsg = 'Error buscando formatos.';
        }
      );
  }

  pickTemplateForPreview(name: string) {
    this.templatePickerSelectedName = name || '';
    if (!this.templatePickerSelectedName) {
      this.templatePickerPreviewTokens = [];
      this.templatePickerPreviewData = null;
      this.templatePickerPreviewMsg = 'Seleccione un formato.';
      return;
    }
    this.templatePickerPreviewLoading = true;
    this.templatePickerPreviewMsg = '';
    let params = new HttpParams()
      .set('name', this.templatePickerSelectedName)
      .set('_ts', Date.now().toString());
    this.http.get<any>(this.globals.apiUrl + '/label_template_load', { params })
      .subscribe(
        (res: any) => {
          this.templatePickerPreviewLoading = false;
          if (!res || !res.ok || !res.data || !res.data.config_json) {
            this.templatePickerPreviewTokens = [];
            this.templatePickerPreviewData = null;
            this.templatePickerPreviewMsg = 'No se pudo cargar vista previa del formato.';
            return;
          }
          try {
            const cfg = JSON.parse(res.data.config_json);
            this.templatePickerPreviewLabelSize = res.data.label_size || '100 x 150 mm';
            this.templatePickerPreviewPrecisionX = Number(res.data.precision_x) || 1;
            this.templatePickerPreviewPrecisionY = Number(res.data.precision_y) || 1;
            const tokens = Array.isArray(cfg.tokens) ? cfg.tokens : [];
            this.templatePickerPreviewTokens = tokens.slice(0, 120);
            this.templatePickerPreviewData = null;
            this.templatePickerPreviewDataMsg = '';
            this.recalculateTemplatePickerActiveBounds();
            this.templatePickerPreviewMsg = '';
          } catch (e) {
            this.templatePickerPreviewTokens = [];
            this.templatePickerPreviewData = null;
            this.templatePickerPreviewMsg = 'Formato inválido.';
          }
        },
        () => {
          this.templatePickerPreviewLoading = false;
          this.templatePickerPreviewTokens = [];
          this.templatePickerPreviewData = null;
          this.templatePickerPreviewMsg = 'Error cargando vista previa.';
        }
      );
  }

  loadTemplatePickerPreviewData() {
    const batchno = Number(this.templatePickerPreviewBatchno);
    const batchseq = Number(this.templatePickerPreviewBatchseq);
    if (!Number.isFinite(batchno) || batchno <= 0) {
      this.templatePickerPreviewDataMsg = 'Indique Nº lote válido.';
      return;
    }

    this.templatePickerPreviewDataLoading = true;
    this.templatePickerPreviewDataMsg = '';
    this.templatePickerPreviewData = null;

    let params = new HttpParams()
      .set('batchno', Math.floor(batchno).toString())
      .set('label_index', '1')
      .set('_ts', Date.now().toString());
    if (Number.isFinite(batchseq) && batchseq > 0) {
      params = params.set('batchseq', Math.floor(batchseq).toString());
    }

    this.http.get<any>(this.globals.apiUrl + '/label_preview_data', { params })
      .subscribe(
        (res: any) => {
          this.templatePickerPreviewDataLoading = false;
          if (res && res.ok && res.data) {
            this.templatePickerPreviewData = res.data;
            this.templatePickerPreviewDataMsg = 'Datos cargados.';
            return;
          }
          this.templatePickerPreviewDataMsg = (res && res.msg) ? res.msg : 'Sin datos para esa referencia.';
        },
        () => {
          this.templatePickerPreviewDataLoading = false;
          this.templatePickerPreviewDataMsg = 'Error cargando datos.';
        }
      );
  }

  applyTemplateFromPicker() {
    if (!this.templatePickerSelectedName) {
      return;
    }
    this.templateName = this.templatePickerSelectedName;
    this.closeTemplatePicker();
    this.loadTemplate();
  }

  getTemplatePickerRowClass(row: LabelTemplateSearchRow): string {
    if (!row) {
      return '';
    }
    return row.name === this.templatePickerSelectedName ? 'is-selected' : '';
  }

  getTemplatePickerDimsMm(): { width: number; height: number } {
    if (this.templatePickerPreviewLabelSize === '100 x 150 mm') {
      return { width: 100, height: 150 };
    }
    if (this.templatePickerPreviewLabelSize === '100 x 100 mm') {
      return { width: 100, height: 100 };
    }
    if (this.templatePickerPreviewLabelSize === '80 x 120 mm') {
      return { width: 80, height: 120 };
    }
    if (this.templatePickerPreviewLabelSize === 'A6') {
      return { width: 105, height: 148 };
    }
    return { width: 148, height: 210 };
  }

  getTemplatePickerScalePxPerMm(): number {
    const dims = this.getTemplatePickerDimsMm();
    const targetWidthPx = 560;
    const targetHeightPx = 640;
    const byWidth = targetWidthPx / Math.max(1, dims.width);
    const byHeight = targetHeightPx / Math.max(1, dims.height);
    const scale = Math.min(byWidth, byHeight);
    return Math.max(2.2, Math.min(4.8, scale));
  }

  getTemplatePickerWidthPx(): number {
    return this.getTemplatePickerDimsMm().width * this.getTemplatePickerScalePxPerMm();
  }

  getTemplatePickerHeightPx(): number {
    return this.getTemplatePickerDimsMm().height * this.getTemplatePickerScalePxPerMm();
  }

  recalculateTemplatePickerActiveBounds() {
    if (!this.templatePickerPreviewTokens || this.templatePickerPreviewTokens.length < 1) {
      this.templatePickerPreviewActiveBounds = { minRow: 0, maxRow: 10, minCol: 0, maxCol: 6 };
      return;
    }
    let minRow = Number.MAX_SAFE_INTEGER;
    let maxRow = 0;
    let minCol = Number.MAX_SAFE_INTEGER;
    let maxCol = 0;
    this.templatePickerPreviewTokens.forEach(token => {
      const rowRaw = Number(token && token.row !== undefined ? token.row : 0);
      const colRaw = Number(token && token.col !== undefined ? token.col : 0);
      const row = Number.isFinite(rowRaw) ? rowRaw : 0;
      const col = Number.isFinite(colRaw) ? colRaw : 0;
      minRow = Math.min(minRow, row);
      maxRow = Math.max(maxRow, row);
      minCol = Math.min(minCol, col);
      maxCol = Math.max(maxCol, col);
    });
    if (!Number.isFinite(minRow) || !Number.isFinite(minCol)) {
      this.templatePickerPreviewActiveBounds = { minRow: 0, maxRow: 10, minCol: 0, maxCol: 6 };
      return;
    }
    minRow = Math.max(0, minRow - 1);
    minCol = Math.max(0, minCol - 1);
    maxRow = maxRow + 1;
    maxCol = maxCol + 1;
    this.templatePickerPreviewActiveBounds = { minRow, maxRow, minCol, maxCol };
  }

  getTemplatePickerBaseCols(): number {
    return Math.max(1, this.templatePickerPreviewActiveBounds.maxCol - this.templatePickerPreviewActiveBounds.minCol + 1);
  }

  getTemplatePickerBaseRows(): number {
    return Math.max(1, this.templatePickerPreviewActiveBounds.maxRow - this.templatePickerPreviewActiveBounds.minRow + 1);
  }

  getTemplatePickerLayoutMetrics(): { marginX: number; marginY: number; cellWidth: number; cellHeight: number } {
    const marginX = 10;
    const marginY = 10;
    const innerW = Math.max(20, this.getTemplatePickerWidthPx() - (marginX * 2));
    const innerH = Math.max(20, this.getTemplatePickerHeightPx() - (marginY * 2));
    return {
      marginX,
      marginY,
      cellWidth: innerW / this.getTemplatePickerBaseCols(),
      cellHeight: innerH / this.getTemplatePickerBaseRows()
    };
  }

  getTemplatePickerTokenLogicalCol(token: LabelTemplateToken): number {
    const gridColRaw = Number(token && token.gridCol !== undefined ? token.gridCol : null);
    if (Number.isFinite(gridColRaw)) {
      return gridColRaw;
    }
    const colRaw = Number(token && token.col !== undefined ? token.col : this.templatePickerPreviewActiveBounds.minCol);
    const col = Number.isFinite(colRaw) ? colRaw : this.templatePickerPreviewActiveBounds.minCol;
    return (col - this.templatePickerPreviewActiveBounds.minCol) * (this.templatePickerPreviewPrecisionX || 1);
  }

  getTemplatePickerTokenLogicalRow(token: LabelTemplateToken): number {
    const gridRowRaw = Number(token && token.gridRow !== undefined ? token.gridRow : null);
    if (Number.isFinite(gridRowRaw)) {
      return gridRowRaw;
    }
    const rowRaw = Number(token && token.row !== undefined ? token.row : this.templatePickerPreviewActiveBounds.minRow);
    const row = Number.isFinite(rowRaw) ? rowRaw : this.templatePickerPreviewActiveBounds.minRow;
    return (row - this.templatePickerPreviewActiveBounds.minRow) * (this.templatePickerPreviewPrecisionY || 1);
  }

  getTemplatePickerTokenLeftPx(token: LabelTemplateToken): number {
    const metrics = this.getTemplatePickerLayoutMetrics();
    return metrics.marginX + (this.getTemplatePickerTokenLogicalCol(token) * metrics.cellWidth) + 2;
  }

  getTemplatePickerTokenTopPx(token: LabelTemplateToken): number {
    const metrics = this.getTemplatePickerLayoutMetrics();
    return metrics.marginY + (this.getTemplatePickerTokenLogicalRow(token) * metrics.cellHeight) + 2;
  }

  getTemplatePickerTokenCaption(token: LabelTemplateToken): string {
    if (!token) {
      return '';
    }
    if (token.title && token.title.trim() !== '') {
      return token.title.trim();
    }
    if (token.text && token.text.trim() !== '') {
      return token.text.trim();
    }
    if (token.dbFieldKey) {
      return this.getDbFieldLabel(token.dbFieldKey);
    }
    return 'Dato';
  }

  getTemplatePickerPreviewValueForToken(token: LabelTemplateToken): string {
    if (!token) {
      return '';
    }
    if (!this.templatePickerPreviewData) {
      return this.getTemplatePickerTokenCaption(token);
    }
    if (!token.dbFieldKey) {
      return token.text || '';
    }
    if (token.dbFieldKey === 'barcodeunits') {
      const current = Number(this.templatePickerPreviewData.label_unit_index) || 1;
      const total = Number(this.templatePickerPreviewData.label_unit_total) || Number(this.templatePickerPreviewData.labels_total) || 1;
      return 'UDS ' + current + '/' + total;
    }
    const value = this.templatePickerPreviewData[token.dbFieldKey];
    if (value === undefined || value === null || value === '') {
      return '';
    }
    return String(value);
  }

  getTemplatePickerTokenDisplayValue(token: LabelTemplateToken): string {
    const raw = this.getTemplatePickerPreviewValueForToken(token);
    const maxLen = Number(token && token.displayLength ? token.displayLength : 0);
    if (maxLen > 0 && raw.length > maxLen && Number(token && token.multiline ? token.multiline : 0) < 2) {
      return raw.substring(0, maxLen);
    }
    if (Number(token && token.multiline ? token.multiline : 0) < 2 || maxLen <= 0) {
      return raw;
    }
    const maxLines = Number(token.multiline);
    const limited = raw.substring(0, maxLen * maxLines);
    const parts: string[] = [];
    for (let i = 0; i < limited.length; i += maxLen) {
      if (parts.length >= maxLines) {
        break;
      }
      parts.push(limited.substring(i, i + maxLen));
    }
    return parts.join('\n');
  }

  getTemplatePickerTokenContainerWidthPx(token: LabelTemplateToken): number {
    const metrics = this.getTemplatePickerLayoutMetrics();
    const base = Math.max(40, metrics.cellWidth - 4);
    const length = Number(token && token.displayLength ? token.displayLength : 0);
    const font = this.getTemplatePickerTokenFontSizePx(token);
    if (length > 0) {
      return Math.max(60, Math.min(420, Math.round((length * font * 0.58) + 12)));
    }
    return Math.max(60, Math.min(260, Math.round(base)));
  }

  getTemplatePickerTokenValueWidthPx(token: LabelTemplateToken): number {
    const title = token && token.title ? token.title.trim() : '';
    if (!title) {
      return this.getTemplatePickerTokenContainerWidthPx(token);
    }
    const font = this.getTemplatePickerTokenFontSizePx(token);
    const titleW = Math.ceil((title.length + 1) * font * 0.55);
    return Math.max(24, this.getTemplatePickerTokenContainerWidthPx(token) - titleW);
  }

  getTemplatePickerTokenLineHeightPx(token: LabelTemplateToken): number {
    const font = this.getTemplatePickerTokenFontSizePx(token);
    return Math.ceil(font * 1.28);
  }

  getTemplatePickerTokenTextMaxHeightPx(token: LabelTemplateToken): number {
    const lines = Math.max(1, Number(token && token.multiline ? token.multiline : 1));
    return Math.max(18, (this.getTemplatePickerTokenLineHeightPx(token) * lines) + 4);
  }

  getTemplatePickerTokenValueAlign(token: LabelTemplateToken): string {
    return this.resolveTokenAlign(token);
  }

  getTemplatePickerTokenTitle(token: LabelTemplateToken): string {
    if (!token || !token.title) {
      return '';
    }
    return token.title.trim();
  }

  getTemplatePickerLogoWidthPx(token: LabelTemplateToken): number {
    return Math.max(12, Number(token && token.logoTargetWidthMm ? token.logoTargetWidthMm : 20) * this.getTemplatePickerScalePxPerMm());
  }

  getTemplatePickerLogoHeightPx(token: LabelTemplateToken): number {
    return Math.max(8, Number(token && token.logoTargetHeightMm ? token.logoTargetHeightMm : 8) * this.getTemplatePickerScalePxPerMm());
  }

  getTemplatePickerBarcodeNumericValue(token: LabelTemplateToken): string {
    const raw = this.getTemplatePickerTokenDisplayValue(token);
    const onlyDigits = (raw || '').replace(/[^0-9]/g, '');
    if (!onlyDigits) {
      return '';
    }
    return onlyDigits.length % 2 === 0 ? onlyDigits : ('0' + onlyDigits);
  }

  getTemplatePickerBarcodeSvgMarkup(token: LabelTemplateToken): string {
    const value = this.getTemplatePickerBarcodeNumericValue(token);
    if (!value) {
      return '';
    }
    if (token && token.barcodeType && token.barcodeType !== 'interleaved_2_of_5') {
      return '';
    }

    const patterns = {
      '0': 'nnwwn',
      '1': 'wnnnw',
      '2': 'nwnnw',
      '3': 'wwnnn',
      '4': 'nnwnw',
      '5': 'wnwnn',
      '6': 'nwwnn',
      '7': 'nnnww',
      '8': 'wnnwn',
      '9': 'nwnwn'
    };

    const narrow = 2;
    const wide = 5;
    const quiet = 10 * narrow;
    const barHeight = 62;
    let x = quiet;
    let bars = '';

    const addBar = (width: number) => {
      bars += '<rect x="' + x + '" y="0" width="' + width + '" height="' + barHeight + '" fill="#000"/>';
      x += width;
    };
    const addSpace = (width: number) => {
      x += width;
    };
    const w = (code: string) => (code === 'w' ? wide : narrow);

    addBar(narrow); addSpace(narrow); addBar(narrow); addSpace(narrow);
    for (let i = 0; i < value.length; i += 2) {
      const a = patterns[value[i]];
      const b = patterns[value[i + 1]];
      for (let j = 0; j < 5; j++) {
        addBar(w(a[j]));
        addSpace(w(b[j]));
      }
    }
    addBar(wide); addSpace(narrow); addBar(narrow);
    x += quiet;

    return '<svg xmlns="http://www.w3.org/2000/svg" width="' + x + '" height="' + barHeight + '" viewBox="0 0 ' + x + ' ' + barHeight + '">' + bars + '</svg>';
  }

  getTemplatePickerBarcodeSvgDataUrl(token: LabelTemplateToken): string {
    const svg = this.getTemplatePickerBarcodeSvgMarkup(token);
    if (!svg) {
      return '';
    }
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
  }

  getTemplatePickerBarcodeSvgSafeUrl(token: LabelTemplateToken): SafeUrl {
    const dataUrl = this.getTemplatePickerBarcodeSvgDataUrl(token);
    if (!dataUrl) {
      return '';
    }
    return this.sanitizer.bypassSecurityTrustUrl(dataUrl);
  }

  getTemplatePickerBarcodeWidthPx(token: LabelTemplateToken): number {
    const maxTextWidth = this.getTemplatePickerTokenContainerWidthPx(token);
    const digits = Math.max(4, this.getTemplatePickerBarcodeNumericValue(token).length);
    const font = Math.max(10, this.getTemplatePickerTokenFontSizePx(token));
    const desired = Math.ceil((digits * 7.2) + (font * 1.8) + 16);
    return Math.max(48, Math.min(maxTextWidth, desired));
  }

  getTemplatePickerBarcodeHeightPx(token: LabelTemplateToken): number {
    const metrics = this.getTemplatePickerLayoutMetrics();
    const font = Math.max(10, this.getTemplatePickerTokenFontSizePx(token));
    const desired = Math.ceil(font * 1.95);
    return Math.max(22, Math.min(74, desired, metrics.cellHeight * 1.8));
  }

  getTemplatePickerBarcodeCaptionFontSize(token: LabelTemplateToken): number {
    const base = this.getTemplatePickerTokenFontSizePx(token);
    return base > 11 ? base - 1 : 10;
  }

  getTemplatePickerBarcodeBlockLeftPx(token: LabelTemplateToken): number {
    const baseLeft = this.getTemplatePickerTokenLeftPx(token);
    const align = this.resolveTokenAlign(token);
    if (align === 'left') {
      return Math.max(2, baseLeft - 8);
    }
    if (align === 'center') {
      return Math.max(2, baseLeft - 4);
    }
    return baseLeft;
  }

  getTemplatePickerTokenFontSizePx(token: LabelTemplateToken): number {
    const base = Number(token && token.fontSize ? token.fontSize : 11);
    return Math.max(10, Math.min(24, Math.round(base * 0.82)));
  }

  getTemplatePickerTokenMaxWidthPx(token: LabelTemplateToken): number {
    const font = this.getTemplatePickerTokenFontSizePx(token);
    const length = Number(token && token.displayLength ? token.displayLength : 0);
    if (length > 0) {
      return Math.max(80, Math.min(340, Math.round((length * font * 0.58) + 12)));
    }
    return Math.max(120, Math.min(280, Math.round(font * 13)));
  }

  normalizeTokenDefaults(token: LabelTemplateToken) {
    if (!token) {
      return;
    }
    token.valueAlign = this.resolveTokenAlign(token);
    if (token.multiline === undefined || token.multiline === null) {
      token.multiline = 0;
    }
    if (token.displayLength === undefined || token.displayLength === null) {
      token.displayLength = 0;
    }
    if (!token.fontFamily) {
      token.fontFamily = 'Arial';
    }
    if (token.isBold === undefined || token.isBold === null) {
      token.isBold = false;
    }
    token.boldScope = this.resolveBoldScope(token);
  }

  normalizeTokensDefaults() {
    if (!this.tokens || this.tokens.length < 1) {
      return;
    }
    this.tokens.forEach(token => this.normalizeTokenDefaults(token));
  }

  resolveTokenAlign(token: LabelTemplateToken): string {
    const raw = token && token.valueAlign !== undefined && token.valueAlign !== null
      ? token.valueAlign.toString().trim().toLowerCase()
      : '';
    if (raw === 'right' || raw === 'derecha') {
      return 'right';
    }
    if (raw === 'center' || raw === 'centrado' || raw === 'centrada') {
      return 'center';
    }
    return 'left';
  }

  resolveBoldScope(token: LabelTemplateToken): string {
    const raw = token && token.boldScope !== undefined && token.boldScope !== null
      ? token.boldScope.toString().trim().toLowerCase()
      : '';
    if (raw === 'title' || raw === 'titulo') {
      return 'title';
    }
    if (raw === 'value' || raw === 'dato') {
      return 'value';
    }
    return 'both';
  }

  cellMapKey(row: number, col: number): string {
    return row + '_' + col;
  }
}
